
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { VideoBlockConfiguration } from "@/types/block-configurations";
import {
  AlignmentControls,
  PaddingControls,
  BorderControls,
  ResponsiveControls,
} from "../shared";

interface VideoBlockPropertiesProps {
  config: VideoBlockConfiguration;
  onChange: (config: VideoBlockConfiguration) => void;
}

export function VideoBlockProperties({ config, onChange }: VideoBlockPropertiesProps) {
  const updateField = <K extends keyof VideoBlockConfiguration>(
    field: K,
    value: VideoBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs font-medium mb-1 block">Provider</Label>
        <Select
          value={config.provider}
          onChange={(e) => updateField('provider', e.target.value as VideoBlockConfiguration['provider'])}
        >
          <option value="native">Native / Direct URL</option>
          <option value="youtube">YouTube</option>
          <option value="vimeo">Vimeo</option>
        </Select>
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">
          Video URL <span className="text-destructive">*</span>
        </Label>
        <Input
          type="url"
          value={config.src}
          onChange={(e) => updateField('src', e.target.value)}
          placeholder={config.provider === 'youtube' ? 'https://youtube.com/watch?v=...' : config.provider === 'vimeo' ? 'https://vimeo.com/...' : 'https://example.com/video.mp4'}
          required
        />
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">
          Fallback Image <span className="text-destructive">*</span>
        </Label>
        <Input
          type="url"
          value={config.thumbnailUrl}
          onChange={(e) => updateField('thumbnailUrl', e.target.value)}
          placeholder="https://example.com/thumbnail.jpg"
        />
        <p className="text-xs text-muted-foreground mt-1">Most email clients don't play video — this image is shown instead. Required for a good fallback experience.</p>
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">Alt Text</Label>
        <Input
          type="text"
          value={config.alt}
          onChange={(e) => updateField('alt', e.target.value)}
          placeholder="Video description"
          maxLength={200}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium mb-1 block">Width (px)</Label>
          <Input
            type="number"
            value={config.width}
            onChange={(e) => updateField('width', parseInt(e.target.value) || 600)}
            min={100}
            max={1200}
          />
        </div>
        <div>
          <Label className="text-xs font-medium mb-1 block">Height (px)</Label>
          <Input
            type="number"
            value={config.height ?? ''}
            onChange={(e) => updateField('height', e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder="Auto (16:9)"
            min={50}
            max={1200}
          />
        </div>
      </div>

      <Accordion type="multiple" className="w-full">

        <AccordionItem value="alignment">
          <AccordionTrigger>Alignment</AccordionTrigger>
          <AccordionContent>
            <AlignmentControls
              value={config.alignment}
              onChange={(alignment) => updateField('alignment', alignment)}
              showVertical={false}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="border">
          <AccordionTrigger>Border & Radius</AccordionTrigger>
          <AccordionContent>
            <BorderControls
              value={config.border}
              onChange={(border) => updateField('border', border)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
